// Chemin: C:\gestion-copro\src\pages\admin-saas\FeedbackSaasPage.tsx
// Session 149 : Gestion des avis et suggestions utilisateurs — Admin SaaS
// Deux onglets : Avis (modération + publication) et Suggestions (suivi)

import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  Star,
  Lightbulb,
  MessageSquare,
  RefreshCw,
  Eye,
  EyeOff,
  Trash2,
  CheckCircle,
  BookOpen,
  Clock,
  Loader2,
  AlertTriangle,
  ThumbsUp,
  Pencil,
  X,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface FeedbackAvis {
  id: string;
  userId: string;
  userEmail: string;
  userPrenom: string;
  userNom: string;
  nomCopropriete: string;
  note: number;
  commentaire: string;
  statut: 'nouveau' | 'approuvé' | 'rejeté';
  publie: boolean;
  createdAt: Date | null;
}

interface FeedbackSuggestion {
  id: string;
  userId: string;
  userEmail: string;
  userPrenom: string;
  userNom: string;
  texte: string;
  statut: 'nouveau' | 'lu' | 'traité';
  createdAt: Date | null;
}

type Onglet = 'avis' | 'suggestions';

// ============================================
// HELPERS
// ============================================

const formatDate = (date: Date | null): string => {
  if (!date) return 'Non renseigné';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const renderEtoiles = (note: number) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`w-4 h-4 ${
            n <= note ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const FeedbackSaasPage: React.FC = () => {
  const [onglet, setOnglet] = useState<Onglet>('avis');
  const [avis, setAvis] = useState<FeedbackAvis[]>([]);
  const [suggestions, setSuggestions] = useState<FeedbackSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editAvis, setEditAvis] = useState<FeedbackAvis | null>(null);
  const [editCommentaire, setEditCommentaire] = useState('');

  // ==========================================
  // CHARGEMENT
  // ==========================================

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les avis
      const avisSnap = await getDocs(
        query(collection(db, 'feedbackAvis'), orderBy('createdAt', 'desc'))
      );
      const avisList: FeedbackAvis[] = avisSnap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          userId: data.userId || '',
          userEmail: data.userEmail || '',
          userPrenom: data.userPrenom || '',
          userNom: data.userNom || '',
          nomCopropriete: data.nomCopropriete || '',
          note: data.note || 0,
          commentaire: data.commentaire || '',
          statut: data.statut || 'nouveau',
          publie: data.publie || false,
          createdAt: data.createdAt?.toDate() || null,
        };
      });
      setAvis(avisList);

      // Charger les suggestions
      const sugSnap = await getDocs(
        query(collection(db, 'feedbackSuggestions'), orderBy('createdAt', 'desc'))
      );
      const sugList: FeedbackSuggestion[] = sugSnap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          userId: data.userId || '',
          userEmail: data.userEmail || '',
          userPrenom: data.userPrenom || '',
          userNom: data.userNom || '',
          texte: data.texte || '',
          statut: data.statut || 'nouveau',
          createdAt: data.createdAt?.toDate() || null,
        };
      });
      setSuggestions(sugList);
    } catch (error) {
      console.error('Erreur chargement feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ==========================================
  // ACTIONS AVIS
  // ==========================================

  const togglePublierAvis = async (avisItem: FeedbackAvis) => {
    setActionLoading(avisItem.id);
    try {
      const newPublie = !avisItem.publie;
      await updateDoc(doc(db, 'feedbackAvis', avisItem.id), {
        publie: newPublie,
        statut: newPublie ? 'approuvé' : 'nouveau',
      });
      setAvis((prev) =>
        prev.map((a) =>
          a.id === avisItem.id
            ? { ...a, publie: newPublie, statut: newPublie ? 'approuvé' : 'nouveau' }
            : a
        )
      );
    } catch (error) {
      console.error('Erreur publication avis:', error);
      alert('Erreur lors de la mise à jour.');
    } finally {
      setActionLoading(null);
    }
  };

  const supprimerAvis = async (avisItem: FeedbackAvis) => {
    if (!window.confirm(`Supprimer l'avis de ${avisItem.userPrenom} ${avisItem.userNom} ?`)) return;
    setActionLoading(avisItem.id);
    try {
      await deleteDoc(doc(db, 'feedbackAvis', avisItem.id));
      setAvis((prev) => prev.filter((a) => a.id !== avisItem.id));
    } catch (error) {
      console.error('Erreur suppression avis:', error);
      alert('Erreur lors de la suppression.');
    } finally {
      setActionLoading(null);
    }
  };

  const ouvrirEditionAvis = (avisItem: FeedbackAvis) => {
    setEditAvis(avisItem);
    setEditCommentaire(avisItem.commentaire);
  };

  const sauvegarderEditionAvis = async () => {
    if (!editAvis) return;
    setActionLoading(editAvis.id);
    try {
      await updateDoc(doc(db, 'feedbackAvis', editAvis.id), {
        commentaire: editCommentaire.trim(),
      });
      setAvis((prev) =>
        prev.map((a) =>
          a.id === editAvis.id ? { ...a, commentaire: editCommentaire.trim() } : a
        )
      );
      setEditAvis(null);
    } catch (error) {
      console.error('Erreur édition avis:', error);
      alert('Erreur lors de la sauvegarde.');
    } finally {
      setActionLoading(null);
    }
  };

  // ==========================================
  // ACTIONS SUGGESTIONS
  // ==========================================

  const changerStatutSuggestion = async (sug: FeedbackSuggestion, newStatut: 'lu' | 'traité') => {
    setActionLoading(sug.id);
    try {
      await updateDoc(doc(db, 'feedbackSuggestions', sug.id), { statut: newStatut });
      setSuggestions((prev) =>
        prev.map((s) => (s.id === sug.id ? { ...s, statut: newStatut } : s))
      );
    } catch (error) {
      console.error('Erreur mise à jour suggestion:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const supprimerSuggestion = async (sug: FeedbackSuggestion) => {
    if (!window.confirm(`Supprimer la suggestion de ${sug.userPrenom} ${sug.userNom} ?`)) return;
    setActionLoading(sug.id);
    try {
      await deleteDoc(doc(db, 'feedbackSuggestions', sug.id));
      setSuggestions((prev) => prev.filter((s) => s.id !== sug.id));
    } catch (error) {
      console.error('Erreur suppression suggestion:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // ==========================================
  // STATS
  // ==========================================

  const nbAvisNouveaux = avis.filter((a) => a.statut === 'nouveau').length;
  const nbAvisPublies = avis.filter((a) => a.publie).length;
  const noteMoyenne = avis.length > 0
    ? (avis.reduce((acc, a) => acc + a.note, 0) / avis.length).toFixed(1)
    : '-';
  const nbSugNouvelles = suggestions.filter((s) => s.statut === 'nouveau').length;

  // ==========================================
  // RENDU
  // ==========================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feedback utilisateurs</h1>
          <p className="text-gray-500">Avis et suggestions des abonnés VIGIE</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Star size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{avis.length}</p>
              <p className="text-xs text-gray-500">Avis total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Eye size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{nbAvisPublies}</p>
              <p className="text-xs text-gray-500">Publiés</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ThumbsUp size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{noteMoyenne}</p>
              <p className="text-xs text-gray-500">Note moyenne</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Lightbulb size={20} className="text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{suggestions.length}</p>
              <p className="text-xs text-gray-500">Suggestions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        <button
          onClick={() => setOnglet('avis')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            onglet === 'avis'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Star size={16} />
          Avis
          {nbAvisNouveaux > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">
              {nbAvisNouveaux}
            </span>
          )}
        </button>
        <button
          onClick={() => setOnglet('suggestions')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            onglet === 'suggestions'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Lightbulb size={16} />
          Suggestions
          {nbSugNouvelles > 0 && (
            <span className="px-1.5 py-0.5 bg-violet-500 text-white text-xs rounded-full">
              {nbSugNouvelles}
            </span>
          )}
        </button>
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={32} className="animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* ======================== ONGLET AVIS ======================== */}
          {onglet === 'avis' && (
            <div className="space-y-3">
              {avis.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <Star size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">Aucun avis pour l'instant</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Les avis apparaîtront ici quand les utilisateurs en laisseront
                  </p>
                </div>
              ) : (
                avis.map((item) => (
                  <div
                    key={item.id}
                    className={`bg-white rounded-xl border p-5 ${
                      item.publie
                        ? 'border-emerald-200 bg-emerald-50/30'
                        : item.statut === 'nouveau'
                        ? 'border-amber-200'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Contenu */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          {renderEtoiles(item.note)}
                          <span className="text-sm font-semibold text-gray-900">
                            {item.note}/5
                          </span>
                          {item.publie && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                              Publié
                            </span>
                          )}
                          {item.statut === 'nouveau' && !item.publie && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              Nouveau
                            </span>
                          )}
                        </div>

                        {item.commentaire && (
                          <p className="text-sm text-gray-700 italic">
                            "{item.commentaire}"
                          </p>
                        )}

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="font-medium text-gray-700">
                            {item.userPrenom} {item.userNom}
                          </span>
                          {item.nomCopropriete && (
                            <span>• {item.nomCopropriete}</span>
                          )}
                          <span>• {formatDate(item.createdAt)}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => ouvrirEditionAvis(item)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors"
                          title="Modifier le commentaire"
                        >
                          <Pencil size={14} />
                          Éditer
                        </button>
                        <button
                          onClick={() => togglePublierAvis(item)}
                          disabled={actionLoading === item.id}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            item.publie
                              ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          }`}
                          title={item.publie ? 'Masquer de la landing' : 'Publier sur la landing'}
                        >
                          {actionLoading === item.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : item.publie ? (
                            <EyeOff size={14} />
                          ) : (
                            <Eye size={14} />
                          )}
                          {item.publie ? 'Masquer' : 'Publier'}
                        </button>
                        <button
                          onClick={() => supprimerAvis(item)}
                          disabled={actionLoading === item.id}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ======================== ONGLET SUGGESTIONS ======================== */}
          {onglet === 'suggestions' && (
            <div className="space-y-3">
              {suggestions.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <Lightbulb size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">Aucune suggestion pour l'instant</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Les suggestions apparaîtront ici quand les utilisateurs en soumettront
                  </p>
                </div>
              ) : (
                suggestions.map((item) => (
                  <div
                    key={item.id}
                    className={`bg-white rounded-xl border p-5 ${
                      item.statut === 'nouveau'
                        ? 'border-violet-200'
                        : item.statut === 'traité'
                        ? 'border-emerald-200 bg-emerald-50/30'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Contenu */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          {item.statut === 'nouveau' && (
                            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">
                              Nouveau
                            </span>
                          )}
                          {item.statut === 'lu' && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                              Lu
                            </span>
                          )}
                          {item.statut === 'traité' && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                              Traité
                            </span>
                          )}
                        </div>

                        <p className="text-sm text-gray-700">{item.texte}</p>

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="font-medium text-gray-700">
                            {item.userPrenom} {item.userNom}
                          </span>
                          <span>• {item.userEmail}</span>
                          <span>• {formatDate(item.createdAt)}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {item.statut === 'nouveau' && (
                          <button
                            onClick={() => changerStatutSuggestion(item, 'lu')}
                            disabled={actionLoading === item.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors"
                          >
                            {actionLoading === item.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <BookOpen size={14} />
                            )}
                            Marquer lu
                          </button>
                        )}
                        {item.statut !== 'traité' && (
                          <button
                            onClick={() => changerStatutSuggestion(item, 'traité')}
                            disabled={actionLoading === item.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg transition-colors"
                          >
                            {actionLoading === item.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <CheckCircle size={14} />
                            )}
                            Traité
                          </button>
                        )}
                        <button
                          onClick={() => supprimerSuggestion(item)}
                          disabled={actionLoading === item.id}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Modal édition avis */}
      {editAvis && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditAvis(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Pencil size={18} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Modifier l'avis</h3>
                  <p className="text-xs text-gray-500">
                    {editAvis.userPrenom} {editAvis.userNom} — {editAvis.note}/5
                  </p>
                </div>
              </div>
              <button onClick={() => setEditAvis(null)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X size={16} className="text-gray-400" />
              </button>
            </div>
            <div className="p-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Commentaire</label>
              <textarea
                value={editCommentaire}
                onChange={(e) => setEditCommentaire(e.target.value)}
                className="w-full h-32 p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2">
                Corrigez les fautes ou modérez le contenu avant publication.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setEditAvis(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={sauvegarderEditionAvis}
                disabled={actionLoading === editAvis.id}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {actionLoading === editAvis.id ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <CheckCircle size={14} />
                )}
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackSaasPage;
